export const NO_COLOR = "inherit";

const removeHash = (hex: string): string => {
  return hex.charAt(0) === "#" ? hex.slice(1) : hex;
};

interface HexObject {
  r: string;
  g: string;
  b: string;
  a: string;
}

function parseHex(nakedHex: string): HexObject {
  const isShort = nakedHex.length === 3 || nakedHex.length === 4;

  const twoDigitHexR = isShort ? `${nakedHex.slice(0, 1)}${nakedHex.slice(0, 1)}` : nakedHex.slice(0, 2);
  const twoDigitHexG = isShort ? `${nakedHex.slice(1, 2)}${nakedHex.slice(1, 2)}` : nakedHex.slice(2, 4);
  const twoDigitHexB = isShort ? `${nakedHex.slice(2, 3)}${nakedHex.slice(2, 3)}` : nakedHex.slice(4, 6);
  const twoDigitHexA = (isShort ? `${nakedHex.slice(3, 4)}${nakedHex.slice(3, 4)}` : nakedHex.slice(6, 8)) || "ff";

  return {
    r: twoDigitHexR,
    g: twoDigitHexG,
    b: twoDigitHexB,
    a: twoDigitHexA,
  };
}

function hexToDecimal(hex: string): number {
  return Number.parseInt(hex, 16);
}

interface DecimalObject {
  r: number;
  g: number;
  b: number;
  a: number;
}

function hexesToDecimals(hexObject: HexObject): DecimalObject {
  const { r, g, b, a } = hexObject;

  return {
    r: hexToDecimal(r),
    g: hexToDecimal(g),
    b: hexToDecimal(b),
    a: Number.parseFloat((hexToDecimal(a) / 255).toFixed(2)),
  };
}

export function hexToRgba(hex: string, a?: string) {
  const hashlessHex = removeHash(hex);
  const hexObject = parseHex(hashlessHex);
  const decimalObject = hexesToDecimals(hexObject);
  return { ...decimalObject, a };
}

export function brightColor(color: string) {
  const { r, g, b } = hexToRgba(color);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

export function brightColorFromShortHex(num: number) {
  const numInt = num | 0;
  // multiply by 0x11 to turn F into FF
  const r = ((numInt >> 8) & 0xf) * 0x11 * 299;
  const g = ((numInt >> 4) & 0xf) * 0x11 * 587;
  const b = ((numInt >> 0) & 0xf) * 0x11 * 114;

  return r + g + b > 128 * 1000;
}

export function brightColorFromLongHex(num: number) {
  const numInt = num | 0;
  // it's actually about 20% faster to shift into a lower range and multiply,
  // than to avoid shifting and multiply by a much larger number
  const r = ((numInt >> 16) & 0xff) * 299;
  const g = ((numInt >> 8) & 0xff) * 587;
  const b = ((numInt >> 0) & 0xff) * 114;

  return r + g + b > 128 * 1000;
}

// this is about 5 times faster than `brightColor`, can replace it eventually
export function brightColorFromHex(color: string) {
  const hashOffset = color.charAt(0) === "#" ? 1 : 0;
  const len = color.length;
  // is a short hex - #FFF or #FFFF (with alpha)
  return len === 3 + hashOffset || len === 4 + hashOffset
    ? brightColorFromShortHex(
        // discard alpha
        Number(`0x${color.slice(hashOffset, 3 + hashOffset)}`),
      )
    : brightColorFromLongHex(
        // discard alpha
        Number(`0x${color.slice(hashOffset, 6 + hashOffset)}`),
      );
}

const white = "#fff";
const black = "#000";

export const invertText = (bg?: string) => {
  if (!bg || bg === "inherit") return "inherit";
  return brightColorFromHex(bg) ? black : white;
};
